import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { User } from '@/api/types';

/**
 * Hooks for the Sprint 7 Evening 8 phone-verification flow. Each
 * mutation invalidates the cached current user so the account page
 * re-reads `phone` / `phoneVerified` from the server instead of
 * guessing — verification state is only known authoritatively from
 * the backend (Twilio Verify + DB).
 *
 * Sprint 7 Evening 10 added profile (firstName/lastName/locale),
 * password change, and notification-preferences hooks at the bottom.
 */

export type NotificationPreferences = {
  emailBookingConfirmation: boolean;
  emailSessionReminder: boolean;
  emailSessionWarning: boolean;
  emailCancellation: boolean;
  emailReceipt: boolean;

  smsBookingConfirmation: boolean;
  smsSessionReminder: boolean;
  smsSessionWarning: boolean;
  smsCancellation: boolean;
};

export function useUpdatePhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { phone: string }) =>
      apiClient<{ message: string; phone: string }>('/users/me/phone', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
}

export function useVerifyPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string }) =>
      apiClient<User>('/users/me/phone/verify', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
}

export function useRemovePhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<{ message: string }>('/users/me/phone', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
}

// ── Sprint 7 Evening 10 ──────────────────────────────────────

/**
 * Profile fields the customer can edit (firstName / lastName /
 * locale). Email is intentionally not editable here — changing
 * email requires re-verification, which Phase 2 ships.
 */
export function useUpdateProfile() {
  return useMutation({
    mutationFn: (body: { firstName?: string; lastName?: string; locale?: string }) =>
      apiClient<User>('/users/me', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  });
}

/**
 * Self-service password rotation. Distinct from the forgotten-
 * password reset flow (which authenticates with a one-time token).
 * Verifying the current password stops a stolen session from
 * locking out the real owner.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      apiClient<{ message: string }>('/users/me/password', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => apiClient<NotificationPreferences>('/users/me/notifications'),
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationPreferences) =>
      apiClient<NotificationPreferences>('/users/me/notifications', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['notification-preferences'], data);
    },
  });
}
